import { useHooks } from '@everlywell/leaves';
import { Box, Button, Flex, Text } from '@everlywell/ui-kit';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useCart } from 'components/Cart';
import { DisclaimerDataProps } from 'components/GeneralDisclaimer';
import { NEW_NAV_HEIGHT } from 'components/Layout';
import { ScreenerDataProps } from 'components/PrePurchaseScreener';
import Price from 'components/Price';
import {
  PurchaseOptionsDrawerProduct,
  useIsPurchaseOptionsEnabled,
  usePurchaseOptionsContext,
} from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import StripePayment, {
  StripePaymentProps,
} from 'components/Stripe/StripePayment';
import { useToastContext } from 'components/ToastContext/ToastContext';
import {
  PrePurchaseModalActionsTypes,
  usePrePurchaseModalContext,
} from 'contexts/PrePurchaseModal';
import { navigate } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useCallback, useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { processExpressPayOrder } from 'utils/api/cartApis';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT_QUEST_BASE_PATH } from 'utils/constants/checkout';
import { VITAMINS } from 'utils/constants/products';
import { ELIMINATION_DIET_COACHING_SLUG } from 'utils/constants/products';
import { logError } from 'utils/helpers';
import { discountedPrice, Promo } from 'utils/helpers/activePromoHelpers';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';
import { notEmpty } from 'utils/types/utilityTypes';

import { useSelectedOptionsContext } from '../../SelectedOption.context';

export type StickyAddToCartBarProps = {
  activePromo: Promo | null;
  showPromo?: boolean;
  product: Queries.pdpProductFragment;
  heroRef: React.RefObject<HTMLDivElement>;
  isVisible?: boolean;
  hasPrePurchaseStep?: boolean;
  screener?: ScreenerDataProps;
  disclaimer?: DisclaimerDataProps;
  isNavbarVisible?: boolean;
  isQuestProduct?: boolean;
  skipPreCart?: boolean;
};

const StickyAddToCartBar = (props: StickyAddToCartBarProps) => {
  const {
    activePromo,
    showPromo,
    product,
    heroRef,
    isVisible = false,
    hasPrePurchaseStep,
    screener,
    disclaimer,
    isNavbarVisible = true,
    isQuestProduct,
    skipPreCart,
  } = props;
  const { spreeProduct } = product;
  const isPurchaseOptionsEnabled = useIsPurchaseOptionsEnabled();
  const purchaseContext = usePurchaseOptionsContext();
  const prePurchaseModalContext = usePrePurchaseModalContext();
  const isEliminationDiet = product?.slug === ELIMINATION_DIET_COACHING_SLUG;
  const selectedOptionContext = useSelectedOptionsContext();

  const flags = useFlags();
  const { expressPayStickyPdp = false, showNewNav: hasNewNavLD } = flags;

  const toast = useToastContext();

  const { addItemToCartAsync, isAddItemToCartLoading } = useCart();

  const { selectedOption } = selectedOptionContext;

  const subscriptionOptionSelected = selectedOption === 'subscription';
  const oneTimePurchaseOptionSelected = selectedOption === 'oneTimePurchase';

  const [hasStripeError, setHasStripeError] = useState(false);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  const handleStripeLoadingError = useCallback((hasError: boolean) => {
    setHasStripeError(hasError);
  }, []);

  useEffect(() => {
    if (!stripePromise) {
      setStripePromise(loadStripe(stripePublicKey));
    }
  }, [stripePromise]);

  const ref = React.useRef<HTMLButtonElement>(null);
  const [buttonHeight, setButtonHeight] = useState<number | null>(null);
  useEffect(() => {
    if (ref.current) {
      setButtonHeight(ref.current.clientHeight);
    }
  }, []);

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        component: 'StickyAddToCartBar',
        product: spreeProduct?.name ?? '',
      },
    });
  }, [spreeProduct?.name]);

  if (!spreeProduct) return null;

  const {
    name,
    variants,
    master_variant_id: productVariantId,
    slug,
    productId,
    promotionable,
  } = spreeProduct;

  const { isMobile } = useHooks.useWindowSize();

  const isVitamin = VITAMINS.includes(slug ?? '');

  const productVariant = variants?.find(
    (variant) => variant?.id === `${productVariantId ?? 0}`,
  );

  const subscriptionVariants =
    variants
      ?.filter((variant) => variant?.subscription_plan)
      .filter(notEmpty) ?? [];

  const singleSubscriptionVariant = subscriptionVariants[0];

  const singleSubscriptionVariantDiscount =
    singleSubscriptionVariant?.subscription_plan?.percent_discount;

  const subscriptionPrice = Number(singleSubscriptionVariant?.price);

  const productPrice = parseFloat(productVariant?.price ?? '0');
  const discountedPromoPrice = parseFloat(
    discountedPrice(
      productVariant?.price ?? '0',
      activePromo ? activePromo : undefined,
    ),
  );

  const showSubscriptionSavings = isVitamin && subscriptionOptionSelected;

  const purchaseDrawerProduct: PurchaseOptionsDrawerProduct = {
    name: name ?? '',
    slug: slug ?? '',
    variantId: productVariantId ?? 0,
    price: productVariant?.price ?? '0',
    subscriptionVariants: (subscriptionVariants ?? []).map((variant) => ({
      id: Number(variant?.id ?? '0'),
      name: variant?.subscription_plan?.name ?? '',
      unit: variant?.subscription_plan?.interval ?? '',
      price: variant?.price ?? '0',
      price_in_dollars: variant?.price ?? '0',
      is_master: variant?.is_master ?? false,
      sku: variant?.sku ?? '',
      subscription_plan: variant?.subscription_plan
        ? {
            id: variant?.subscription_plan?.id ?? 0,
            name: variant?.subscription_plan?.name ?? '',
            interval: variant?.subscription_plan?.interval ?? '',
            interval_count: variant?.subscription_plan?.interval_count ?? 0,
            percent_discount: variant?.subscription_plan?.percent_discount ?? 0,
          }
        : undefined,
    })),
    hideSubscription: false,
    productId: productId ?? 0,
    promotionable: promotionable ?? false,
  };

  const handleScrollToHero = () => {
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // ### Cart interactions

  const handleAddToCart = async ({ variantId }: { variantId: number }) => {
    try {
      await addItemToCartAsync({
        variant_id: variantId,
        quantity: 1,
      });
    } catch (error) {
      logError('There was an issue adding the item to the cart', {
        component: 'StickyAddToCartBar',
        method: 'handleAddToCart',
        experience: 'PDP',
      });
    }
  };

  const handleStickyAddToCartBarClick = async () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: isQuestProduct
          ? ANALYTICS.LABELS.QUEST_BUY_TEST
          : ANALYTICS.LABELS.ADD_TO_CART,
        component: 'StickyAddToCartBar',
        product: spreeProduct.name ?? '',
      },
    });
    if (isQuestProduct) {
      navigate(`${CHECKOUT_QUEST_BASE_PATH}/?variant=${productVariantId}`);
      return;
    }
    if (skipPreCart) {
      await handleAddToCart({ variantId: purchaseDrawerProduct?.variantId });
      return;
    }
    if (isPurchaseOptionsEnabled.isEnabled) {
      if (hasPrePurchaseStep) {
        prePurchaseModalContext?.dispatch({
          type: PrePurchaseModalActionsTypes.SetPrePurchaseScreener,
          payload: {
            isPrePurchaseScreenerOpen: true,
            prePurchaseScreenerData: screener,
            generalDisclaimerData: disclaimer,
          },
        });
      } else {
        purchaseContext?.setProduct(purchaseDrawerProduct);
      }
    } else {
      //go to pdp hero section for desktop
      handleScrollToHero();
    }
  };

  let displayPrice;

  if (oneTimePurchaseOptionSelected) {
    displayPrice = showPromo ? discountedPromoPrice : productPrice;
  } else if (subscriptionOptionSelected) {
    displayPrice = subscriptionPrice;
  } else {
    displayPrice = productPrice;
  }

  // ### Express Pay
  // express pay total is in cents
  // note: express pay only works for one time purchase, but regardless if it changes in the future, we should charge what the user sees
  const expressPayTotal = displayPrice * 100;

  /* unable to test stripe in our unit tests at the moment */
  /* istanbul ignore next */
  const handleExpressPayPaymentConfirm: StripePaymentProps['handlePaymentConfirm'] =
    async (data, token) => {
      try {
        if (productVariantId) {
          const response = await processExpressPayOrder({
            type: 'singleItem',
            variantId: productVariantId,
            promoCode: showPromo ? activePromo?.code ?? undefined : undefined,
            data,
            token,
          });
          navigate(
            `/confirmation?orderId=${response.data.id}&registration_token=${response.data.registration_token}&referral=true`,
          );
        }
      } catch (e: any) {
        const error = e as unknown as Error;
        let errorMessage =
          'Oops! Something went wrong. Please try again or contact support for help';
        if (!/(network|syntax)/i.test(error.message)) {
          errorMessage = error.message;
        }
        toast.showToast({
          message: errorMessage,
          status: 'error',
        });
        logError('Error processing express pay payment', {
          component: 'PurchaseOptions',
          method: 'handleExpressPayPaymentConfirm',
          experience: 'PDP',
        });
      }
    };

  return (
    <Box
      className="ju_forcepush"
      visibility={isVisible ? 'visible' : 'hidden'}
      backgroundColor={['tints.lightCream', 'tints.white']}
      paddingX={6}
      paddingY={4}
      top={
        isNavbarVisible
          ? hasNewNavLD
            ? NEW_NAV_HEIGHT
            : ['68px', null, '58px']
          : 0
      }
      left={0}
      position={'fixed'}
      zIndex={2}
      width={'100%'}
      boxShadow={['2xl', 'none']}
      borderBottomRadius={['24px', 0]}
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
    >
      {/* left side - product name */}
      <Box display={['none', 'block']}>
        <Flex flexDirection={'row'}>
          <Text size="lg" fontWeight={'medium'}>
            {name}
          </Text>
        </Flex>
      </Box>
      {/* product price and cta */}
      <Flex
        flexDirection={'row'}
        gap={6}
        width={['100%', 'fit-content']}
        justifyContent={'space-between'}
      >
        {/* product price plus promotion */}
        <Flex alignItems={'center'}>
          <Flex flexDirection="column" alignItems={'baseline'} gap={0.5}>
            <Flex
              flexDirection={['column', 'row']}
              alignItems={['flex-start', 'center']}
              gap={[0, 1]}
              width={'100%'}
              flexGrow={1}
            >
              <Flex
                alignItems={'baseline'}
                gap={2}
                width={'100%'}
                flexWrap={'wrap'}
              >
                <Box color={showPromo ? 'utility.sale' : 'inherit'}>
                  <Price price={displayPrice} />
                </Box>
                {((oneTimePurchaseOptionSelected && showPromo) ||
                  subscriptionOptionSelected) &&
                  !isVitamin && (
                    <Box color={'tints.darkGrey'}>
                      <Price
                        price={productPrice}
                        strikeThrough
                        fontSize={'xl'}
                      />
                    </Box>
                  )}
              </Flex>
              {(!showPromo || isVitamin || (showPromo && !isMobile)) && (
                <UnitLabel
                  showSubscriptionSavings={showSubscriptionSavings}
                  singleSubscriptionVariantDiscount={
                    singleSubscriptionVariantDiscount
                  }
                />
              )}
            </Flex>
            {((oneTimePurchaseOptionSelected && showPromo) ||
              subscriptionOptionSelected) && (
              <PromoCallout
                isVitamin={isVitamin}
                isMobile={isMobile}
                productPrice={productPrice}
                promo={activePromo}
                showSubscriptionSavings={showSubscriptionSavings}
                subscriptionDiscount={singleSubscriptionVariantDiscount ?? 0}
              />
            )}
          </Flex>
        </Flex>
        <Flex
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}
        >
          <Button
            ref={ref}
            width={{ base: '100px', tablet: '134px' }}
            variant={'primary'}
            colorScheme="greenCS"
            onClick={handleStickyAddToCartBarClick}
            data-testid="stickyAddToCartButton"
            isLoading={isAddItemToCartLoading}
          >
            Buy
          </Button>
          {expressPayStickyPdp &&
            (selectedOption === 'oneTimePurchase' || isEliminationDiet) &&
            !hasStripeError &&
            buttonHeight && (
              <Box
                flex={1}
                width={{ base: '100px', tablet: '134px' }}
                className="stripe-payment-pdp-sticky-add-to-cart"
              >
                <Elements stripe={stripePromise}>
                  <StripePayment
                    buttonHeight={buttonHeight}
                    cartTotal={expressPayTotal}
                    label={ANALYTICS.LABELS.EXPRESS_PAY_PDP}
                    variantsSkus={spreeProduct?.sku ? [spreeProduct.sku] : []}
                    onStripeLoadingError={handleStripeLoadingError}
                    withApplePayWidget={false}
                    handlePaymentConfirm={handleExpressPayPaymentConfirm}
                  />
                </Elements>
              </Box>
            )}
        </Flex>
      </Flex>
    </Box>
  );
};

const UnitLabel = (props: {
  showSubscriptionSavings: boolean;
  singleSubscriptionVariantDiscount: number | null | undefined;
}) => {
  const { showSubscriptionSavings, singleSubscriptionVariantDiscount } = props;
  const frequencyUnit = showSubscriptionSavings ? 'month' : 'each';
  return (
    <Text
      color={'tints.darkGrey'}
      fontSize={'sm'}
      fontWeight={'normal'}
      marginTop={[0, 1.5]}
    >
      /{frequencyUnit}
      {showSubscriptionSavings && (
        <Box display={['inline', 'none']}>
          <Text as="span" color={'tints.darkGrey'} fontSize={'sm'}>
            {' '}
            ·
          </Text>
          {singleSubscriptionVariantDiscount && (
            <Text as="span" color={'utility.sale'} fontSize={'sm'}>
              {' '}
              Save {singleSubscriptionVariantDiscount}%
            </Text>
          )}
        </Box>
      )}
    </Text>
  );
};

const PromoCallout = (props: {
  isVitamin: boolean;
  isMobile: boolean;
  productPrice: number;
  promo: Promo | null;
  showSubscriptionSavings?: boolean;
  subscriptionDiscount?: number;
}) => {
  const {
    isVitamin,
    isMobile,
    promo,
    showSubscriptionSavings,
    subscriptionDiscount,
  } = props;
  if (isVitamin && isMobile) return null;
  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      alignItems={'baseline'}
      width={'100%'}
      gap={1}
    >
      <Text
        color={'utility.sale'}
        fontSize={'sm'}
        fontWeight={'normal'}
        marginTop={'1px'}
      >
        {showSubscriptionSavings
          ? `Save ${subscriptionDiscount}%`
          : `with code ${promo?.code?.toUpperCase()}`}
      </Text>
    </Box>
  );
};

export default StickyAddToCartBar;
